import { useEffect, useState, type ComponentType } from 'react'
import {
  BookOpen,
  ChartColumn,
  ChevronLeft,
  ChevronRight,
  Ellipsis,
  LogOut,
  ScanEye,
  FileText,
  Gift,
  LayoutGrid,
  Moon,
  Nfc,
  Presentation,
  Radio,
  Route,
  SlidersHorizontal,
  Sparkles,
  Sun,
  type LucideProps,
  Megaphone,
} from 'lucide-react'
import { Fehlergrenze } from './components/Fehlergrenze'
import { ShowcasesPage } from './pages/ShowcasesPage'
import { DocumentsPage } from './pages/DocumentsPage'
import { KnowledgePage } from './pages/KnowledgePage'
import { NfcTagsPage } from './pages/NfcTagsPage'
import { PersonaPage } from './pages/PersonaPage'
import { ConfigPage } from './pages/ConfigPage'
import { AnalyticsPage } from './pages/AnalyticsPage'
import { RecognitionPage } from './pages/RecognitionPage'
import { TourPage } from './pages/TourPage'
import { LivePage } from './pages/LivePage'
import { GewinnspielPage } from './pages/GewinnspielPage'
import { PraesentationPage } from './pages/PraesentationPage'
import { AnsagenPage } from './pages/AnsagenPage'
import { applyTheme, followSystemTheme, readTheme, type Theme } from './lib/theme'
import { useLogout } from './components/LoginGate'
import { NurLesenBanner } from './components/NurLesenBanner'
import { SyncHinweis } from './components/SyncHinweis'

interface Tab {
  id: string
  label: string
  /** Shorter label for the phone tab bar. */
  short: string
  icon: ComponentType<LucideProps>
  /** Icon tile colour, like the grouped lists in the iOS/macOS settings. */
  tile: string
  /** Glyph colour on the tile. */
  glyph: string
}

/**
 * Grouped like a settings sidebar: what visitors see, how K4ia behaves, how
 * it went. The tiles use the j4p palette; the label next to each tile carries
 * the meaning, the colour only helps finding it again.
 */
const GROUPS: { title: string; tabs: Tab[] }[] = [
  {
    title: 'Inhalte',
    tabs: [
      {
        id: 'showcases',
        label: 'Showcases',
        short: 'Showcases',
        icon: LayoutGrid,
        tile: '#63c3d1',
        glyph: '#1d1d1f',
      },
      {
        id: 'knowledge',
        label: 'Wissen',
        short: 'Wissen',
        icon: BookOpen,
        tile: '#1d1d1f',
        glyph: '#ffffff',
      },
      {
        id: 'documents',
        label: 'Dokumente',
        short: 'Dokumente',
        icon: FileText,
        tile: '#f08046',
        glyph: '#ffffff',
      },
      {
        id: 'tour',
        label: 'Rundgang',
        short: 'Rundgang',
        icon: Route,
        tile: '#0f766e',
        glyph: '#ffffff',
      },
      {
        id: 'nfc',
        label: 'NFC-Tags',
        short: 'NFC',
        icon: Nfc,
        tile: '#17727e',
        glyph: '#ffffff',
      },
      {
        id: 'gewinnspiel',
        label: 'Gewinnspiel',
        short: 'Gewinnspiel',
        icon: Gift,
        tile: '#c2410c',
        glyph: '#ffffff',
      },
      {
        id: 'praesentation',
        label: 'Präsentation',
        short: 'Präsentation',
        icon: Presentation,
        tile: '#1e40af',
        glyph: '#ffffff',
      },
      {
        id: 'ansagen',
        label: 'Ansagen',
        short: 'Ansagen',
        icon: Megaphone,
        tile: '#b45309',
        glyph: '#ffffff',
      },
    ],
  },
  {
    title: 'K4ia',
    tabs: [
      {
        id: 'persona',
        label: 'Persönlichkeit',
        short: 'Persona',
        icon: Sparkles,
        tile: '#d2436d',
        glyph: '#ffffff',
      },
      {
        id: 'config',
        label: 'Konfiguration',
        short: 'Einstellungen',
        icon: SlidersHorizontal,
        tile: '#636366',
        glyph: '#ffffff',
      },
    ],
  },
  {
    title: 'Auswertung',
    tabs: [
      {
        id: 'live',
        label: 'Live',
        short: 'Live',
        icon: Radio,
        tile: '#b91c1c',
        glyph: '#ffffff',
      },
      {
        id: 'analytics',
        label: 'Analytics',
        short: 'Analytics',
        icon: ChartColumn,
        tile: '#047857',
        glyph: '#ffffff',
      },
      {
        id: 'recognition',
        label: 'Erkennung',
        short: 'Erkennung',
        icon: ScanEye,
        tile: '#8e6bd6',
        glyph: '#ffffff',
      },
    ],
  },
]

const TABS = GROUPS.flatMap((g) => g.tabs)

/** Was am Handy direkt in der Tab-Leiste steht — der Rest liegt unter „Mehr“. */
const PRIMARY = ['showcases', 'knowledge', 'live', 'analytics'] as const
const MORE = 'more'

/**
 * Der Bereich steht in der Adresse (`#/config`): Neu laden bleibt auf der
 * Seite, und die Zurück-Geste am Handy führt zum vorigen Bereich statt aus
 * dem Admin heraus.
 */
function readTab(): string {
  // `#/showcases/sc_x` gehört zu „showcases“ — der Rest ist Sache der Seite.
  const id = window.location.hash.replace(/^#\/?/, '').split('/')[0] ?? ''
  return id === MORE || TABS.some((t) => t.id === id) ? id : 'showcases'
}

function useTab(): [string, (id: string) => void] {
  const [tab, setTabState] = useState(readTab)
  useEffect(() => {
    const onHash = () => setTabState(readTab())
    window.addEventListener('hashchange', onHash)
    return () => window.removeEventListener('hashchange', onHash)
  }, [])
  const setTab = (id: string) => {
    if (id === readTab()) {
      window.scrollTo({ top: 0, behavior: 'smooth' })
      return
    }
    window.location.hash = `/${id}`
  }
  // Neuer Bereich beginnt oben — sonst landet man mitten in der nächsten Seite.
  // Mit Klammern, ohne Rückgabe: Neuere Browser geben bei scrollTo ein Promise
  // zurück, und React hielt das für die Aufräumfunktion — beim nächsten
  // Seitenwechsel „n is not a function“ und ein abgestürztes Admin (05.10.).
  useEffect(() => {
    window.scrollTo(0, 0)
  }, [tab])
  return [tab, setTab]
}

export function App() {
  const [tab, setTab] = useTab()
  const logout = useLogout()
  const current = TABS.find((t) => t.id === tab)
  const onPrimary = (PRIMARY as readonly string[]).includes(tab)

  return (
    <div className="min-h-screen md:flex">
      {/* Sidebar (tablet and up) */}
      <nav
        aria-label="Bereiche"
        className="glass sticky top-0 hidden h-screen w-72 shrink-0 flex-col px-4 py-6 md:flex"
      >
        <Brand />
        <div className="mt-8 flex-1 space-y-6 overflow-y-auto">
          {GROUPS.map((g) => (
            <div key={g.title}>
              <p className="mb-2 px-3 font-mono text-[0.7rem] uppercase tracking-wider text-text-muted">
                {g.title}
              </p>
              <ul className="space-y-0.5">
                {g.tabs.map((t) => (
                  <li key={t.id}>
                    <NavItem tab={t} active={tab === t.id} onSelect={() => setTab(t.id)} />
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </div>
        <div className="mt-4 flex items-center justify-between gap-2 px-3 pt-4">
          <span className="text-xs text-text-muted">J4F VisionLab</span>
          <div className="flex items-center gap-2">
            {logout && (
              <button
                type="button"
                onClick={logout}
                aria-label="Abmelden"
                title="Abmelden"
                className="flex h-9 w-9 items-center justify-center rounded-full bg-surface-2 text-text-muted transition-colors hover:text-text"
              >
                <LogOut size={17} aria-hidden="true" />
              </button>
            )}
            <ThemeToggle />
          </div>
        </div>
      </nav>

      {/* Top bar (phone): Zurück zu „Mehr“, wenn man von dort kam */}
      <header className="glass sticky top-0 z-20 flex min-h-[3.25rem] items-center justify-between gap-3 px-4 pb-2 pt-[max(env(safe-area-inset-top),0.5rem)] md:hidden">
        {current && !onPrimary ? (
          <button
            type="button"
            onClick={() => setTab(MORE)}
            className="-ml-2 flex min-h-[2.75rem] items-center gap-0.5 pr-3 text-[0.95rem] font-medium text-brand"
          >
            <ChevronLeft size={22} aria-hidden="true" />
            Mehr
          </button>
        ) : (
          <Brand compact />
        )}
        <ThemeToggle />
      </header>

      <main className="min-w-0 flex-1 px-4 pb-[calc(var(--tabbar-h)+2rem)] pt-5 sm:px-6 md:px-10 md:pb-12 md:pt-10">
        <div className="mx-auto max-w-5xl">
          <NurLesenBanner />
          <SyncHinweis onZurKonfiguration={() => setTab('config')} />
          {/* Je Bereich neu (key): Ein Absturz bleibt in seiner Seite, die Navigation bleibt. */}
          <Fehlergrenze key={tab}>
            {tab === MORE && <MorePage onSelect={setTab} logout={logout} />}
            {tab === 'showcases' && <ShowcasesPage />}
            {tab === 'knowledge' && <KnowledgePage />}
            {tab === 'documents' && <DocumentsPage />}
            {tab === 'nfc' && <NfcTagsPage />}
            {tab === 'tour' && <TourPage />}
            {tab === 'gewinnspiel' && <GewinnspielPage />}
            {tab === 'praesentation' && <PraesentationPage />}
            {tab === 'ansagen' && <AnsagenPage />}
            {tab === 'live' && <LivePage />}
            {tab === 'persona' && <PersonaPage />}
            {tab === 'config' && <ConfigPage />}
            {tab === 'analytics' && <AnalyticsPage />}
            {tab === 'recognition' && <RecognitionPage />}
          </Fehlergrenze>
        </div>
      </main>

      {/* Tab bar (phone): vier Bereiche und „Mehr“ — zehn passten nicht nebeneinander */}
      <nav
        aria-label="Bereiche"
        className="glass fixed inset-x-0 bottom-0 z-20 flex justify-around border-t border-border px-2 pb-[max(env(safe-area-inset-bottom),0.35rem)] pt-1.5 md:hidden"
      >
        {PRIMARY.map((id) => {
          const t = TABS.find((x) => x.id === id)!
          return (
            <TabBarItem
              key={id}
              icon={t.icon}
              label={t.short}
              active={tab === id}
              onSelect={() => setTab(id)}
            />
          )
        })}
        <TabBarItem
          icon={Ellipsis}
          label="Mehr"
          active={!onPrimary}
          onSelect={() => setTab(MORE)}
        />
      </nav>
    </div>
  )
}

function TabBarItem({
  icon: Icon,
  label,
  active,
  onSelect,
}: {
  icon: ComponentType<LucideProps>
  label: string
  active: boolean
  onSelect: () => void
}) {
  return (
    <button
      type="button"
      onClick={onSelect}
      aria-current={active ? 'page' : undefined}
      className={`flex min-h-[3rem] min-w-0 flex-1 flex-col items-center justify-center gap-0.5 rounded-xl text-[0.7rem] font-medium ${
        active ? 'text-brand' : 'text-text-muted'
      }`}
    >
      <Icon size={24} strokeWidth={active ? 2.25 : 1.75} aria-hidden="true" />
      <span className="truncate">{label}</span>
    </button>
  )
}

/**
 * „Mehr“ am Handy: alle Bereiche als Liste wie in den iOS-Einstellungen,
 * gruppiert wie die Seitenleiste am Rechner — dazu Erscheinungsbild und
 * Abmelden, die sonst unten in der Seitenleiste stehen.
 */
function MorePage({
  onSelect,
  logout,
}: {
  onSelect: (id: string) => void
  logout: (() => void) | null
}) {
  return (
    <div className="space-y-7">
      <div className="flex items-center gap-3">
        <img
          src="/brand/k4ia-logo.png"
          alt=""
          width={52}
          height={52}
          className="rounded-full shadow-[var(--shadow-card)]"
        />
        <div>
          <h2 className="page-title">K4ia Admin</h2>
          <p className="text-sm text-text-muted">J4F VisionLab</p>
        </div>
      </div>
      {GROUPS.map((g) => (
        <section key={g.title} className="space-y-2">
          <p className="px-4 font-mono text-[0.7rem] uppercase tracking-wider text-text-muted">
            {g.title}
          </p>
          <ul className="card divide-y divide-border overflow-hidden">
            {g.tabs.map((t) => {
              const Icon = t.icon
              return (
                <li key={t.id}>
                  <button
                    type="button"
                    onClick={() => onSelect(t.id)}
                    className="flex min-h-[3.25rem] w-full items-center gap-3 px-4 text-left text-[1.0625rem] active:bg-surface-2"
                  >
                    <span
                      aria-hidden="true"
                      className="flex h-8 w-8 shrink-0 items-center justify-center rounded-lg ring-1 ring-inset ring-border"
                      style={{ background: t.tile, color: t.glyph }}
                    >
                      <Icon size={18} strokeWidth={2.25} />
                    </span>
                    <span className="flex-1">{t.label}</span>
                    <ChevronRight size={18} className="text-text-muted" aria-hidden="true" />
                  </button>
                </li>
              )
            })}
          </ul>
        </section>
      ))}
      {logout && (
        <ul className="card overflow-hidden">
          <li>
            <button
              type="button"
              onClick={logout}
              className="flex min-h-[3.25rem] w-full items-center gap-3 px-4 text-left text-[1.0625rem] text-status-error active:bg-surface-2"
            >
              <LogOut size={20} aria-hidden="true" />
              Abmelden
            </button>
          </li>
        </ul>
      )}
    </div>
  )
}

function Brand({ compact = false }: { compact?: boolean }) {
  return (
    <div className="flex items-center gap-3 px-2">
      <img
        src="/brand/k4ia-logo.png"
        alt=""
        width={compact ? 32 : 40}
        height={compact ? 32 : 40}
        className="rounded-full shadow-[var(--shadow-card)]"
      />
      <div className="leading-tight">
        <h1 className={compact ? 'text-base' : 'text-lg'}>K4ia</h1>
        {!compact && <p className="text-xs text-text-muted">Admin</p>}
      </div>
    </div>
  )
}

function NavItem({ tab, active, onSelect }: { tab: Tab; active: boolean; onSelect: () => void }) {
  const Icon = tab.icon
  return (
    <button
      type="button"
      onClick={onSelect}
      aria-current={active ? 'page' : undefined}
      className={`flex w-full items-center gap-3 rounded-xl px-3 py-2 text-left text-[0.95rem] transition-colors ${
        active ? 'bg-surface-2 font-semibold text-text' : 'text-text hover:bg-surface-2/60'
      }`}
    >
      <span
        aria-hidden="true"
        className="flex h-7 w-7 shrink-0 items-center justify-center rounded-lg ring-1 ring-inset ring-border"
        style={{ background: tab.tile, color: tab.glyph }}
      >
        <Icon size={16} strokeWidth={2.25} />
      </span>
      {tab.label}
    </button>
  )
}

function ThemeToggle() {
  const [theme, setTheme] = useState<Theme>(readTheme)
  const next: Theme = theme === 'dark' ? 'light' : 'dark'
  // Folgt dem System, bis jemand den Schalter tippt (lib/theme.ts).
  useEffect(() => {
    const abmelden = followSystemTheme(setTheme)
    return abmelden
  }, [])
  return (
    <button
      type="button"
      onClick={() => {
        applyTheme(next)
        setTheme(next)
      }}
      aria-label={next === 'light' ? 'Hellen Modus einschalten' : 'Dunklen Modus einschalten'}
      title={next === 'light' ? 'Heller Modus' : 'Dunkler Modus'}
      className="flex h-9 w-9 items-center justify-center rounded-full bg-surface-2 text-text transition-colors hover:text-brand"
    >
      {next === 'light' ? (
        <Sun size={18} aria-hidden="true" />
      ) : (
        <Moon size={18} aria-hidden="true" />
      )}
    </button>
  )
}
